import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import CallexLoader from '../../../../components/CallexLoader';
import Pagination from '../../../../components/form/Pagination';
import AdminHeader from '../../../../components/header/AdminHeader';
import DeleteConfirmationModal from '../../../../components/Modals/DeleteConfirmationModal';

import {
  outletDelete,
  outletListGet,
  useOutletList
} from '../../../../store/slices/tenantSlices/productSlice';
import AddEditOutletModal from './AddEditOutletModal';
import ImportOutletModal from './ImportOutletModal';
import { checkModulePermission } from '../../../../utils/common';
import { useUserRolePermission } from '../../../../store/slices/authSlice';

const ProductOutlet = () => {
  const dispatch = useDispatch();
  const beatList = useOutletList();
  const userRolePermission = useUserRolePermission();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchProductOutletList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(outletListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteProductOutlet = async () => {
    try {
      const response = await dispatch(outletDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (beatList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchProductOutletList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchProductOutletList();
  }, [filter]);

  return (
    <div>
      <AdminHeader
        title="Outlet"
        isExpandSearch
        isAdd
        onAddClick={() => setOpenAddModal(true)}
        isImport
        isImportDisabled={!checkModulePermission('is_import', 'Outlet', userRolePermission)}
        isAddDisabled={!checkModulePermission('is_add', 'Outlet', userRolePermission)}
        onImportClick={() => setOpenImportModal(true)}
        addBtnName="Add Outlet"
        searchPlaceholder="Search Outlet"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : beatList?.data?.length > 0 ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Beat Name</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {beatList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.name}</td>
                      <td>{val?.beat_name}</td>
                      <td>{String(val?.status)}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Outlet', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_edit', 'Outlet', userRolePermission)
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission('is_delete', 'Outlet', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_delete', 'Outlet', userRolePermission)
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={beatList?.total}
              limit={beatList?.limit}
              page={beatList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchProductOutletList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteProductOutlet()}
      />
      <AddEditOutletModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchProductOutletList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
      <ImportOutletModal
        isOpen={openImportModal}
        onCloseClick={(apiCall = false) => {
          setOpenImportModal(false);
          if (apiCall) {
            fetchProductOutletList();
          }
        }}
      />
    </div>
  );
};

export default ProductOutlet;
